import { ImportIcon, MoonIcon, SunIcon } from "./Icons";

export type AppTab = "analyze" | "train" | "explore" | "games" | "me";

interface AppBarProps {
  tab: AppTab;
  onTab: (tab: AppTab) => void;
  engineState: "idle" | "loading" | "ready" | "error";
  depth: number | null;
  progress: { done: number; total: number };
  onImport: () => void;
  theme: "light" | "dark";
  onToggleTheme: () => void;
}

const TAB_LABELS: Array<{ id: AppTab; label: string }> = [
  { id: "analyze", label: "Analyze" },
  { id: "train", label: "Train" },
  { id: "explore", label: "Explore" },
  { id: "games", label: "Games" },
  { id: "me", label: "Me" },
];

export function AppBar({
  tab,
  onTab,
  engineState,
  depth,
  progress,
  onImport,
  theme,
  onToggleTheme,
}: AppBarProps) {
  const chipClass =
    engineState === "ready"
      ? "chip chip--live"
      : engineState === "loading"
        ? "chip chip--busy"
        : engineState === "error"
          ? "chip chip--error"
          : "chip";

  const chipLabel =
    engineState === "ready"
      ? `Stockfish depth ${depth ?? "—"}`
      : engineState === "loading"
        ? `Analysing ${progress.done}/${progress.total}`
        : engineState === "error"
          ? "Engine error"
          : "Engine idle";

  return (
    <header className="appbar">
      <div className="brand">
        <span className="brand__mark">
          Onchain<em>Reps</em>
        </span>
        <span className="brand__tag">free chess analysis</span>
      </div>

      <nav className="appbar__nav" aria-label="Sections">
        {TAB_LABELS.map((entry) => (
          <button
            key={entry.id}
            onClick={() => onTab(entry.id)}
            aria-current={tab === entry.id ? "true" : undefined}
          >
            {entry.label}
          </button>
        ))}
      </nav>

      <span className={chipClass}>
        <span className="chip__dot" />
        {chipLabel}
      </span>

      <button
        className="iconbtn"
        onClick={onToggleTheme}
        aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        title={theme === "dark" ? "Light theme" : "Dark theme"}
      >
        {theme === "dark" ? <SunIcon /> : <MoonIcon />}
      </button>

      <button className="iconbtn iconbtn--primary" onClick={onImport} aria-label="Import a game">
        <ImportIcon />
      </button>
    </header>
  );
}
